import React from "react";
import { Badge, Box, HStack, Icon, Text } from "@chakra-ui/react";
import { MdEdit, MdWarning } from "react-icons/md";

const DEFAULT_VARIANT = "gray";
const CHAKRA_COLOR_NAME_RE = /^[a-z][a-z0-9]*$/i;

function getVariantColors(variant) {
  const raw = typeof variant === "string" ? variant.trim() : "";
  const color = CHAKRA_COLOR_NAME_RE.test(raw) ? raw : DEFAULT_VARIANT;
  return {
    bg: `${color}.100`,
    color: `${color}.600`,
    darkBg: `${color}.800`,
    darkColor: `${color}.200`,
  };
}

function isIncompleteText(value) {
  const text = String(value ?? "").replace(/\s+/g, " ").trim().toLowerCase();
  if (!text) return true;
  if (/^(-|—)+$/.test(text)) return true;
  if (/^sin\b/.test(text)) return true;
  if (/^no hay\b/.test(text)) return true;
  if (/^actividad sin\b/.test(text)) return true;
  return false;
}

function hasIncompleteValue(node) {
  if (node === null || node === undefined || typeof node === "boolean") return false;
  if (Array.isArray(node)) return node.some(hasIncompleteValue);
  if (typeof node === "string") return isIncompleteText(node);
  if (typeof node === "number") return false;
  if (React.isValidElement(node)) return hasIncompleteValue(node.props?.children);
  return false;
}

export function DataCard({ title, headerIcon, variant = "gray", onEdit, editAriaLabel, children, isIncomplete, ...boxProps }) {
  const v = getVariantColors(variant);
  const showIncomplete = typeof isIncomplete === "boolean" ? isIncomplete : hasIncompleteValue(children);
  const clickable = typeof onEdit === "function";

  function handleCardKeyDown(e) {
    if (!clickable) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onEdit();
    }
  }

  return (
    <Box
      bg="dashboard.bg.surface"
      color="dashboard.fg.default"
      rounded="card"
      borderWidth="1px"
      borderColor={showIncomplete ? "red.500" : "dashboard.border.muted"}
      _dark={{ borderColor: showIncomplete ? "red.700" : undefined }}
      overflow="hidden"
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      aria-label={clickable ? (editAriaLabel ?? `Editar ${title}`) : undefined}
      onClick={clickable ? onEdit : undefined}
      onKeyDown={clickable ? handleCardKeyDown : undefined}
      cursor={clickable ? "pointer" : "default"}
      transition="box-shadow 150ms ease, transform 150ms ease, border-color 150ms ease"
      _hover={clickable ? { transform: "translateY(-2px)", boxShadow: "cardLift", borderColor: showIncomplete ? "red.400" : "dashboard.border.default" } : undefined}
      _focusVisible={{ outline: "2px solid", outlineColor: "dashboard.accent.border", outlineOffset: "2px" }}
      {...boxProps}
    >
      {/* Header */}
      <HStack
        px="5"
        pt="4"
        pb="3"
        gap="3"
        justify="space-between"
        borderBottomWidth="1px"
        borderColor="dashboard.border.muted"
      >
        <HStack gap="2.5" minW="0" flex="1">
          <Box
            display="grid"
            placeItems="center"
            boxSize="7"
            rounded="lg"
            flexShrink={0}
            bg={v.bg}
            color={v.color}
            _dark={{ bg: v.darkBg, color: v.darkColor }}
          >
            <Icon boxSize="4">{headerIcon}</Icon>
          </Box>
          <Text fontWeight="700" fontSize="sm" color="dashboard.fg.default" lineClamp={1}>
            {title}
          </Text>
        </HStack>

        <HStack gap="2" flexShrink={0}>
          {showIncomplete && (
            <Badge colorPalette="red" variant="subtle" size="sm" gap="1">
              <Icon fontSize="10px"><MdWarning /></Icon>
              Incompleto
            </Badge>
          )}
          {clickable && (
            <Icon as={MdEdit} boxSize="3.5" color="dashboard.fg.subtle" />
          )}
        </HStack>
      </HStack>

      {/* Body */}
      <Box p="5">
        {children}
      </Box>
    </Box>
  );
}
